/**
 * Kasa Device Card
 *
 * Displays a single Kasa/Tapo device with status, controls, and energy data.
 */

import React from 'react';
import { useKasaStore, KasaDevice, KasaEnergyReading } from '../kasa-store';
import { TransferDeviceModal } from './TransferDeviceModal';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import {
  Plug,
  ToggleLeft,
  ToggleRight,
  Lightbulb,
  Power,
  Zap,
  Wifi,
  WifiOff,
  Pencil,
  Check,
  X,
  ArrowRightLeft,
  FileText,
  Cpu,
} from 'lucide-react';

const deviceIcons: Record<string, React.ReactNode> = {
  plug: <Plug className="h-5 w-5" />,
  switch: <ToggleLeft className="h-5 w-5" />,
  dimmer: <Lightbulb className="h-5 w-5" />,
  bulb: <Lightbulb className="h-5 w-5" />,
  strip: <Plug className="h-5 w-5" />,
  unknown: <Power className="h-5 w-5" />,
};

const statusConfig = {
  on: { color: 'bg-emerald-500', label: 'On', icon: <ToggleRight className="h-4 w-4" /> },
  off: { color: 'bg-muted-foreground/50', label: 'Off', icon: <ToggleLeft className="h-4 w-4" /> },
  unreachable: { color: 'bg-destructive', label: 'Unreachable', icon: <WifiOff className="h-4 w-4" /> },
};

interface DeviceCardProps {
  device: KasaDevice;
  energy?: KasaEnergyReading;
}

export const DeviceCard: React.FC<DeviceCardProps> = ({ device, energy }) => {
  const {
    controlDevice, setBrightness, controlESP32Device, setESP32Brightness,
    renameESP32Device, renameDevice, updateDeviceDescription, updateESP32DeviceDescription, isControlling,
  } = useKasaStore();
  const [localBrightness, setLocalBrightness] = React.useState<number | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editName, setEditName] = React.useState('');
  const [isEditingDesc, setIsEditingDesc] = React.useState(false);
  const [editDesc, setEditDesc] = React.useState('');
  const [showTransferModal, setShowTransferModal] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const descRef = React.useRef<HTMLTextAreaElement>(null);

  const isESP32 = device._source === 'esp32';
  const displayName = device.customAlias || device.alias;
  const config = statusConfig[device.status] || statusConfig.off;
  const isOn = device.status === 'on';
  const isReachable = device.status !== 'unreachable';

  const handleToggle = () => {
    if (isESP32) {
      controlESP32Device(device.deviceId, 'toggle');
    } else {
      controlDevice(device.deviceId, 'toggle');
    }
  };

  const handleBrightnessChange = (value: number[]) => {
    setLocalBrightness(value[0]);
  };

  const handleBrightnessCommit = (value: number[]) => {
    if (isESP32) {
      setESP32Brightness(device.deviceId, value[0]);
    } else {
      setBrightness(device.deviceId, value[0]);
    }
    setLocalBrightness(null);
  };

  const handleStartEdit = () => {
    setEditName(displayName);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleSaveEdit = async () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== displayName) {
      if (isESP32) {
        await renameESP32Device(device.deviceId, trimmed);
      } else {
        await renameDevice(device.deviceId, trimmed);
      }
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveEdit();
    if (e.key === 'Escape') handleCancelEdit();
  };

  const handleStartDescEdit = () => {
    setEditDesc(device.description || '');
    setIsEditingDesc(true);
    setTimeout(() => descRef.current?.focus(), 0);
  };

  const handleSaveDesc = async () => {
    const trimmed = editDesc.trim();
    if (trimmed !== (device.description || '')) {
      if (isESP32) {
        await updateESP32DeviceDescription(device.deviceId, trimmed);
      } else {
        await updateDeviceDescription(device.deviceId, trimmed);
      }
    }
    setIsEditingDesc(false);
  };

  const handleDescKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveDesc(); }
    if (e.key === 'Escape') setIsEditingDesc(false);
  };

  return (
    <Card className={`transition-all ${isOn ? 'ring-1 ring-primary/25' : ''}`}>
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className={`p-1.5 rounded-md shrink-0 ${isOn ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
              {deviceIcons[device.deviceType]}
            </div>
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <Input
                    ref={inputRef}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSaveEdit}
                    className="h-6 text-sm px-1.5 py-0"
                    autoFocus
                  />
                  <button onClick={handleSaveEdit} className="text-primary hover:text-primary/80 shrink-0">
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button onMouseDown={(e) => { e.preventDefault(); handleCancelEdit(); }} className="text-muted-foreground hover:text-foreground shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 group">
                  <h3 className="font-medium text-sm leading-tight truncate">{displayName}</h3>
                  <button onClick={handleStartEdit} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0">
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground truncate">{device.model} &middot; {device.host}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-2">
            {isESP32 && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/40 text-primary">
                ESP32
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {device.protocol.toUpperCase()}
            </Badge>
            <div className={`w-2 h-2 rounded-full ${config.color}`} />
          </div>
        </div>

        {/* Description */}
        {(
          <div className="group/desc">
            {isEditingDesc ? (
              <div className="space-y-1">
                <textarea
                  ref={descRef}
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  onKeyDown={handleDescKeyDown}
                  onBlur={handleSaveDesc}
                  placeholder="Add a description for AI agents..."
                  className="w-full text-xs px-2 py-1.5 border rounded-md resize-none bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                  rows={2}
                  maxLength={500}
                />
                <div className="flex items-center gap-1 justify-end">
                  <button onClick={handleSaveDesc} className="text-primary hover:text-primary/80">
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button onMouseDown={(e) => { e.preventDefault(); setIsEditingDesc(false); }} className="text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={handleStartDescEdit}
                className="flex items-start gap-1 cursor-pointer rounded px-1 -mx-1 py-0.5 hover:bg-muted/50 transition-colors"
              >
                <FileText className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground" />
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {device.description || <span className="italic opacity-0 group-hover/desc:opacity-100 transition-opacity">Add description...</span>}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Power toggle + Transfer */}
        <div className="flex items-center gap-2">
          <Button
            variant={isOn ? 'default' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={handleToggle}
            disabled={!isReachable || isControlling}
          >
            {config.icon}
            <span className="ml-1">{isOn ? 'Turn Off' : 'Turn On'}</span>
          </Button>
          {!isESP32 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTransferModal(true)}
              title="Transfer to another organization"
              className="px-2"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Brightness slider */}
        {device.supportsBrightness && isReachable && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Brightness</span>
              <span>{localBrightness ?? device.brightness ?? 100}%</span>
            </div>
            <Slider
              value={[localBrightness ?? device.brightness ?? 100]}
              min={1}
              max={100}
              step={1}
              onValueChange={handleBrightnessChange}
              onValueCommit={handleBrightnessCommit}
              disabled={isControlling}
            />
          </div>
        )}

        {/* Energy data */}
        {energy && device.supportsEmeter && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t">
            <div className="flex items-center gap-1">
              <Zap className="h-3 w-3 text-yellow-500" />
              <span>{(energy.power_mw / 1000).toFixed(1)}W</span>
            </div>
            <div>
              <span>{(energy.voltage_mv / 1000).toFixed(0)}V</span>
            </div>
            <div>
              <span>{(energy.current_ma / 1000).toFixed(2)}A</span>
            </div>
            <div className="ml-auto">
              <span>{(energy.total_wh / 1000).toFixed(2)} kWh</span>
            </div>
          </div>
        )}

        {/* Connection info */}
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          {isReachable ? (
            <Wifi className="h-3 w-3" />
          ) : (
            <WifiOff className="h-3 w-3 text-destructive" />
          )}
          <span>Last seen {new Date(device.lastSeen).toLocaleTimeString()}</span>
          {(device.controllerDeviceId || device._discoveredBy) && (
            <span className="ml-auto flex items-center gap-1 text-primary" title={`Controlled by ${device.controllerDeviceId || device._discoveredBy}`}>
              <Cpu className="h-3 w-3" />
              <span className="truncate max-w-[100px]">{(device.controllerDeviceId || device._discoveredBy || '').replace('rpi-', 'Pi:').replace('esp32-', 'ESP:')}</span>
            </span>
          )}
        </div>
      </CardContent>

      {showTransferModal && (
        <TransferDeviceModal
          device={device}
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
        />
      )}
    </Card>
  );
};
